import { AnyAction } from 'redux';
import {
  GlobalActions,
  GlobalUnfallVpUebernehmenAction,
  GlobalVorbelegungForBeibehaltungRequestedAction,
} from '@ps20-frontend/shop-integration-redux';
import { convertDateFormatFromShortIsoToLocalState, isYoungerThan } from '@ps20-frontend/utils';
import { UnfallVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { Anrede } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { UnfActions } from '../unfActions';
import { isDateInRange } from '../selector/produkt.selector';
import { Berufsrisikogruppe, getBerufsrisikogruppeByValue } from '../model/unf.enums';

export const vpReducer = function (
  sectionState: IAppState['vp'] = null,
  action: AnyAction,
  state: IAppState,
): IAppState['vp'] {
  switch (action.type) {
    case GlobalActions.DASHBOARD_UNFALL_VP_UEBERNEHMEN:
      return loadVp(sectionState, action as GlobalUnfallVpUebernehmenAction, state);
    case UnfActions.BERUFSRISIKOGRUPPE_CHANGED:
      return { ...sectionState, berufsrisikogruppe: action.payload };
    case UnfActions.SPECIAL_SPORT_RISK_DATA_CHANGED:
      return { ...sectionState, profiSport: action.payload };
    case GlobalActions.VERSICHERUNGSBEGINN_CHANGED:
      return modifyVp(sectionState, action, state);
    case GlobalActions.PREISE_RESET_DASHBOARD:
      return resetBerufsrisikogruppe(sectionState, action, state);
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED:
      return loadVpFromVorvertrag(
        sectionState,
        action as GlobalVorbelegungForBeibehaltungRequestedAction,
        state,
      );
    default:
      return sectionState;
  }
};

function loadVp(
  sectionState: IAppState['vp'],
  action: GlobalUnfallVpUebernehmenAction,
  state: IAppState,
): IAppState['vp'] {
  if (action.payload.angebotsId === state.angebotsId) {
    const {
      anrede,
      vorname,
      nachname,
      geburtsdatum,
      akadgrad,
      adelsbez,
      adelstitel,
      juristisch,
      kundenverwaltungID,
      kundennummer,
      adresse,
    } = action.payload.vp;
    return {
      anrede,
      vorname,
      nachname,
      geburtsdatum,
      akadgrad,
      adelsbez,
      adelstitel,
      juristisch,
      kundenverwaltungID,
      kundennummer,
      adresse,
      isVn: action.payload.isVn,
      berufsrisikogruppe: null,
    };
  }
  return sectionState;
}

function modifyVp(
  sectionState: IAppState['vp'],
  action: AnyAction,
  state: IAppState,
): IAppState['vp'] {
  if (action.payload.angebotsId === state.angebotsId) {
    const oldBeginnInRange = isDateInRange(
      sectionState.geburtsdatum,
      state.vertragsoptionen.beginnDatum,
      1,
      17,
    );
    const newBeginnInRange = isDateInRange(sectionState.geburtsdatum, action.payload.beginn, 1, 17);
    if (newBeginnInRange !== oldBeginnInRange) {
      return {
        ...sectionState,
        berufsrisikogruppe: null,
      } as IAppState['vp'];
    }
  }
  return sectionState;
}

function resetBerufsrisikogruppe(
  sectionState: IAppState['vp'],
  action: AnyAction,
  state: IAppState,
): IAppState['vp'] {
  if (action.payload.angebotsId === state.angebotsId) {
    return {
      ...sectionState,
      berufsrisikogruppe: null,
    } as IAppState['vp'];
  }
  return sectionState;
}

function loadVpFromVorvertrag(
  sectionState: IAppState['vp'],
  action: GlobalVorbelegungForBeibehaltungRequestedAction,
  state: IAppState,
): IAppState['vp'] {
  if (action.payload.angebotsId === state.angebotsId) {
    const vorbelegungsdaten = action.payload as UnfallVorbelegungdaten;
    return {
      ...sectionState,
      geburtsdatum: convertDateFormatFromShortIsoToLocalState(action.payload.geburtsdatum),
      berufsrisikogruppe: getBerufsrisikogruppeFromVorvertrag(vorbelegungsdaten),
      anrede: vorbelegungsdaten.anrede as Anrede,
      vorname: vorbelegungsdaten.vorname,
      nachname: vorbelegungsdaten.nachname,
      adresse: vorbelegungsdaten.vpAdresse,
    };
  }
  return sectionState;
}

function getBerufsrisikogruppeFromVorvertrag(
  vorbelegungsdaten: UnfallVorbelegungdaten,
): Berufsrisikogruppe {
  const geburtsdatum = convertDateFormatFromShortIsoToLocalState(vorbelegungsdaten.geburtsdatum);
  const stichTag = convertDateFormatFromShortIsoToLocalState(vorbelegungsdaten.beginnDatum);
  if (vorbelegungsdaten.berufsrisikogruppe) {
    return getBerufsrisikogruppeByValue(vorbelegungsdaten.berufsrisikogruppe.toString());
  } else if (isYoungerThan(geburtsdatum, stichTag, 17)) {
    return Berufsrisikogruppe.KAUFMAENNISCH;
  } else {
    return null;
  }
}
